/* @flow */
/**
 * Redux Toolkit slice for PO change report search criteria, saved searches,
 * results, and pagination.
 */
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import request, { cancelRequest } from '../../services/request';
import {
  updateDialogStates,
  toggleSpinner, togglePaginationSpinner
} from '../dialogStates/dialogStatesSlice';
import getflattenResponse, { cryptoRand } from '../../helpers/common';
import poChangeReportsHeader from '../../constants/poChangeReportHeader';
import { initialPOChangeReportFieldState, poChangeReportFieldList } from '../../constants/staticData';
import {
  mapInputFieldsToRequest, mapRequestToInputFields, checkForColumnData, setValidFields
} from '../../helpers/dataHelpers';
import appConfig from '../../appConfig';
import poChangeReportFieldProperties from '../../constants/fieldProperties/poChangeReportFieldProperties';

export const name = 'poChangeReport';

function createInitialState() {
  return {
    isSavedSearchModified: false,
    poChangeReportRequestBody: null,
    poChangeReportInputFieldData: { ...initialPOChangeReportFieldState },
    poChangeReportSelectedFields: [],
    poChangeReportBookmarkName: '',
    poChangeReportBookmarkId: '',
    requestError: false,

    poChangeReportResultData: {},
    poChangeReportSearchId: '',
    poChangeReportTotalCount: 0,
    poChangeReportNextResults: {},
    poChangeReportPrevResults: {},
    page: 0,
    rowsPerPage: 100,
    jumpToPage: 0,
    apiCallAfterAppend: false,
    apiCallBeforeAppend: false
  };
}

export const initialState = createInitialState();

/**
 * Updates the PO change report page index.
 */
function updatePOChangeReportPage(state: Object, action: Object) {
  // UPDATE_PO_CHANGE_REPORT_PAGE
  return { ...state, page: action.payload };
}

/**
 * Updates the PO change report page size.
 */
function updatePOChangeReportRowsPerPage(state: Object, action: Object) {
  // UPDATE_PO_CHANGE_REPORT_ROWS_PER_PAGE
  return { ...state, rowsPerPage: action.payload };
}

/**
 * Stores the PO change report request body and marks saved searches as modified when it changes.
 */
function updatePOChangeReportRequestBody(state: Object, action: Object) {
  // UPDATE_PO_CHANGE_REPORT_REQUEST_BODY
  // Compare serialized request bodies to detect saved-search edits.
  const requestExist = JSON.stringify(state.poChangeReportRequestBody);
  return {
    ...state,
    poChangeReportRequestBody: action.payload,
    isSavedSearchModified: Boolean(
      state.poChangeReportBookmarkId && requestExist !== JSON.stringify(action.payload)
    )
  };
}

/**
 * Sets whether the active saved PO change report search has unsaved changes.
 */
function updateIsSavedSearchModified(state: Object, action: Object) {
  return {
    ...state,
    isSavedSearchModified: Boolean(action.payload)
  };
}

/**
 * Replaces the PO change report request body without changing bookmark metadata.
 */
function updatePOChangeReportRequestBodyOnSameBookmark(state: Object, action: Object) {
  // UPDATE_POCHANGE_SEARCH_REQUEST_ON_SAME_BOOKMARK
  return {
    ...state,
    poChangeReportRequestBody: action.payload
  };
}

/**
 * Clears PO change report results and pagination state.
 */
function resetPoChangeReportResults(state: Object) {
  // RESET_PO_CHANGE_REPORT_RESULTS
  return {
    ...state,
    poChangeReportResultData: {},
    poChangeReportSearchId: '',
    poChangeReportTotalCount: 0,
    poChangeReportNextResults: {},
    poChangeReportPrevResults: {},
    page: 0,
    rowsPerPage: 100
  };
}

/**
 * Resets PO change report search criteria and saved-search metadata.
 */
function resetPoChangeReportRequest(state: Object) {
  // RESET_PO_CHANGE_REPORT_REQUEST
  return {
    ...state,
    isSavedSearchModified: false,
    poChangeReportRequestBody: null,
    poChangeReportInputFieldData: { ...initialPOChangeReportFieldState },
    poChangeReportSelectedFields: [],
    poChangeReportBookmarkName: '',
    poChangeReportBookmarkId: '',
    requestError: false
  };
}

/**
 * Clears PO change report search criteria while preserving selected field uniqueness.
 */
function resetPOChangeReportRequestForClearSearch(state: Object) {
  // RESET_PO_CHANGE_REPORT_REQUEST_DATA_CLEAR_SEARCH
  return {
    ...state,
    isSavedSearchModified: false,
    poChangeReportRequestBody: null,
    poChangeReportInputFieldData: { ...initialPOChangeReportFieldState },
    poChangeReportSelectedFields: [...new Set(state.poChangeReportSelectedFields)],
    poChangeReportBookmarkName: '',
    poChangeReportBookmarkId: '',
    requestError: false
  };
}

/**
 * Stores selected PO change report field keys.
 */
function updatePOChangeReportFields(state: Object, action: Object) {
  // UPDATE_PO_CHANGE_REPORT_SELETED_FIELDS
  return {
    ...state, poChangeReportSelectedFields: action.payload
  };
}

/**
 * Stores PO change report input field values.
 */
function updatePOChangeReportInputValues(state: Object, action: Object) {
  // UPDATE_PO_CHANGE_REPORT_INPUT_VALUES
  return { ...state, poChangeReportInputFieldData: action.payload };
}

/**
 * Stores the active PO change report saved-search ID.
 */
function updatePOChangeReportBookmarkId(state: Object, action: Object) {
  // UPDATE_PO_CHANGE_REPORT_BOOKMARK_ID
  return { ...state, isSavedSearchModified: false, poChangeReportBookmarkId: action.payload };
}

/**
 * Clears active PO change report saved-search metadata.
 */
function clearSavedPOChangeReport(state: Object) {
  // CLEAR_PO_CHANGE_REPORT_BOOKMARK
  return {
    ...state,
    isSavedSearchModified: false,
    poChangeReportBookmarkId: '',
    poChangeReportBookmarkName: ''
  };
}

/**
 * Stores the active PO change report saved-search name.
 */
function updatePOChangeReportBookmarkName(state: Object, action: Object) {
  // UPDATE_PO_CHANGE_REPORT_BOOKMARK_NAME
  return { ...state, isSavedSearchModified: false, poChangeReportBookmarkName: action.payload };
}

/**
 * Stores flattened PO change report results and resets adjacent result buffers.
 */
function updatePOChangeReportResults(state: Object, action: Object) {
  // UPDATE_PO_CHANGE_REPORT_RESULTS
  const payload = {
    // Flatten nested API objects into the grid-friendly result shape.
    data: getflattenResponse(action.payload.data),
    dataNext: null
  };

  return {
    ...state,
    poChangeReportResultData: payload.data,
    poChangeReportTotalCount: payload.data.pages.totalResources,
    poChangeReportSearchId: cryptoRand().toString().substr(2, 8),
    selectedRecordsCount: 0,
    poChangeReportNextResults: null,
    poChangeReportPrevResults: null
  };
}

/**
 * Refreshes the PO change report search identifier used to trigger UI updates.
 */
function updatePOChangeReportSearchId(state: Object) {
  // UPDATE_PO_CHANGE_REPORT_SEARCH_ID
  return { ...state, poChangeReportSearchId: cryptoRand().toString().slice(2, 10) };
}

/**
 * Restores PO change report input fields to their initial values.
 */
function resetPOChangeReportInputData(state: Object) {
  // RESET_PO_CHANGE_REPORT_INPUT_VALUES
  return { ...state, poChangeReportInputFieldData: initialState.poChangeReportInputFieldData };
}

/**
 * Stores the current and prefetched next PO change report result sets.
 */
function updatePOChangeReportNextResult(state: Object, action: Object) {
  // UPDATE_PO_CHANGE_REPORT_NEXT_RESULTS
  const payload = {
    data: getflattenResponse(action.payload.currentResultSet),
    dataNext: getflattenResponse(action.payload.response.data)
  };

  return {
    ...state,
    poChangeReportResultData: payload.data,
    poChangeReportNextResults: payload.dataNext,
    poChangeReportTotalPages: payload.data.pages.totalPages,
    poChangeReportTotalCount: payload.data.pages.totalResources
  };
}

/**
 * Stores the current and prefetched previous PO change report result sets.
 */
function updatePOChangeReportPrevResult(state: Object, action: Object) {
  // UPDATE_PO_CHANGE_REPORT_PREV_RESULTS
  const payload = {
    data: getflattenResponse(action.payload.currentResultSet),
    dataPrev: getflattenResponse(action.payload.response.data)
  };

  return {
    ...state,
    poChangeReportResultData: payload.data,
    poChangeReportPrevResults: payload.dataPrev,
    poChangeReportTotalPages: payload.data.pages.totalPages,
    poChangeReportTotalCount: payload.data.pages.totalResources
  };
}

/**
 * Stores PO change report results fetched from the first result offset.
 */
function PoChangeReportOffset(state: Object, action: Object) {
  // PO_CHANGE_REPORT_RESULT_OFFSET
  const payload = {
    data: getflattenResponse(action.payload.data)
  };

  return {
    ...state,
    poChangeReportResultData: payload?.data,
    poChangeReportTotalCount: payload?.data?.pages?.totalResources,
    poChangeReportSearchId: cryptoRand().toString().slice(2, 10),
    selectedRecordsCount: 0,
    poChangeReportNextResults: null,
    poChangeReportPrevResults: null
  };
}

/**
 * Promotes the prefetched next PO change report result set into the current page window.
 */
function ChangeReportAppendNextResultSet(state: Object) {
  // UPDATE_PO_CHANGE_REPORT_NEXT_APPEND
  const payload = {
    dataPrev: state.poChangeReportResultData,
    data: state.poChangeReportNextResults
  };

  return {
    ...state,
    poChangeReportResultData: payload.data,
    poChangeReportPrevResults: payload.dataPrev,
    poChangeReportNextResults: null
  };
}

/**
 * Promotes next jump-to-page results and updates prefetch flags for page-window boundaries.
 */
function ChangeReportAppendJumpToPageNextResultSet(state: Object, action: Object) {
  // UPDATE_PO_CHANGE_REPORT_NEXT_APPEND
  const payload = {
    dataPrev: state.poChangeReportResultData,
    data: state.poChangeReportNextResults
  };

  // Page-window boundary checks depend on the visible page index and page size.
  const numberString = state.page.toString();

  const rowPerPage = action.payload;

  let checkApiCallApend = false;
  let checkApiCallPrepend = false;

  if (Number(rowPerPage) === 100) {
    checkApiCallApend = numberString[numberString.length - 1] === '9';
    checkApiCallPrepend = numberString[numberString.length - 1] === '0';
  } else if (Number(rowPerPage) === 250) {
    checkApiCallApend = (Number(numberString) + 1) % 4 === 0;
    checkApiCallPrepend = Number(numberString) % 4 === 0;
  } else if (Number(rowPerPage) === 500) {
    checkApiCallApend = (Number(numberString) + 1) % 2 === 0;
    checkApiCallPrepend = Number(numberString) % 2 === 0;
  }

  return {
    ...state,
    poChangeReportResultData: payload.data,
    poChangeReportPrevResults: payload.dataPrev,
    poChangeReportNextResults: null,
    apiCallAfterAppend: checkApiCallApend,
    apiCallBeforeAppend: checkApiCallPrepend
  };
}

/**
 * Promotes the prefetched previous PO change report result set into the current page window.
 */
function ChangeReportAppendPrevResultSet(state: Object) {
  // UPDATE_PO_CHANGE_REPORT_PREV_APPEND
  const payload = {
    data: state.poChangeReportPrevResults,
    dataNext: state.poChangeReportResultData
  };

  return {
    ...state,
    poChangeReportResultData: payload.data,
    poChangeReportNextResults: payload.dataNext,
    poChangeReportPrevResults: null
  };
}

/**
 * Promotes previous jump-to-page results and updates prefetch flags for page-window boundaries.
 */
function ChangeReportAppendJumpToPagePrevResultSet(state: Object, action: Object) {
  // UPDATE_PO_CHANGE_REPORT_PREV_APPEND
  const payload = {
    data: state.poChangeReportPrevResults,
    dataNext: state.poChangeReportResultData
  };

  // Page-window boundary checks depend on the visible page index and page size.
  const numberString = state.page.toString();

  const rowPerPage = action.payload;

  let checkApiCallApend = false;
  let checkApiCallPrepend = false;

  if (Number(rowPerPage) === 100) {
    checkApiCallApend = numberString[numberString.length - 1] === '9';
    checkApiCallPrepend = numberString[numberString.length - 1] === '0';
  } else if (Number(rowPerPage) === 250) {
    checkApiCallApend = (Number(numberString) + 1) % 4 === 0;
    checkApiCallPrepend = Number(numberString) % 4 === 0;
  } else if (Number(rowPerPage) === 500) {
    checkApiCallApend = (Number(numberString) + 1) % 2 === 0;
    checkApiCallPrepend = Number(numberString) % 2 === 0;
  }

  return {
    ...state,
    poChangeReportResultData: payload.data,
    poChangeReportNextResults: payload.dataNext,
    poChangeReportPrevResults: null,
    apiCallAfterAppend: checkApiCallApend,
    apiCallBeforeAppend: checkApiCallPrepend
  };
}

/**
 * Clears the prefetched next PO change report result set.
 */
function updateNextResultSettoNull(state: Object) {
  return {
    ...state,
    poChangeReportNextResults: null
  };
}

/**
 * Clears the prefetched previous PO change report result set.
 */
function updatePreviousResultSettoNull(state: Object) {
  return {
    ...state,
    poChangeReportPrevResults: null
  };
}

/**
 * Stores a requested jump-to-page index and marks the jump trigger.
 */
function updateJumpToPage(state: Object, action: Object) {
  return {
    ...state,
    jumpToPage: action.payload,
    triggerJumpToPage: true
  };
}

/**
 * Updates whether pagination is processing a jump-to-page request.
 */
function updateTriggerJumpToPage(state: Object, action: Object) {
  return {
    ...state,
    triggerJumpToPage: action.payload
  };
}

/**
 * Updates whether another next-page API call is needed after appending results.
 */
function updateApiCallAfterAppend(state: Object, action: Object) {
  return {
    ...state,
    apiCallAfterAppend: action.payload
  };
}

/**
 * Updates whether another previous-page API call is needed after prepending results.
 */
function updateApiCallBeforePrepend(state: Object, action: Object) {
  return {
    ...state,
    apiCallBeforeAppend: action.payload
  };
}

/**
 * Collects reducers that manage PO change report state.
 */
function createReducers() {
  return {
    updatePOChangeReportPage,
    updatePOChangeReportRowsPerPage,
    updatePOChangeReportRequestBody,
    updateIsSavedSearchModified,
    updatePOChangeReportRequestBodyOnSameBookmark,
    resetPoChangeReportResults,
    resetPoChangeReportRequest,
    resetPOChangeReportRequestForClearSearch,
    updatePOChangeReportFields,
    updatePOChangeReportInputValues,
    updatePOChangeReportBookmarkId,
    clearSavedPOChangeReport,
    updatePOChangeReportBookmarkName,
    updatePOChangeReportResults,
    updatePOChangeReportSearchId,
    resetPOChangeReportInputData,
    updatePOChangeReportNextResult,
    updatePOChangeReportPrevResult,
    PoChangeReportOffset,
    ChangeReportAppendNextResultSet,
    ChangeReportAppendPrevResultSet,
    ChangeReportAppendJumpToPageNextResultSet,
    ChangeReportAppendJumpToPagePrevResultSet,
    updateJumpToPage,
    updateTriggerJumpToPage,
    updateApiCallAfterAppend,
    updateApiCallBeforePrepend,
    updatePreviousResultSettoNull,
    updateNextResultSettoNull
  };
}

export const reducers = createReducers();
export const slice = createSlice({ name, initialState, reducers });

const actions = { ...slice.actions };

/**
 * Creates a thunk that searches PO change reports using poChangeReportSearch.
 */
function fetchPoChangeReports() {
  return createAsyncThunk(
    `${name}/fetchPoChangeReports`,
    async ({
      data,
      callback,
      modifiedrequestBody = {}
    }, { getState, dispatch }) => {
      dispatch(toggleSpinner(true));
      const secondaryFields = [];
      const fields = poChangeReportsHeader
        .filter((header) => header.default)
        .map((header) => {
          if (header.secondary) {
            secondaryFields.push(header.secondary);
          }
          return header.primary;
        });
      const state = getState();
      let requestData = {};
      const {
        selectedColumnOrderOptions: columnOrderOptions,
        rowPerPageOption: rowsPerPageOption
      } = state.searchpaneldata || {};

      // Prefer user-selected column order; otherwise request default headers and secondary fields.
      const fieldsForRequest = checkForColumnData(state, true)
        ? columnOrderOptions
        : [...fields, ...secondaryFields];
      if (data) {
        requestData = mapInputFieldsToRequest(data, 'poChangeReport');
        const appliedPoChangeSearchField = new Set(
          requestData.map((field) => field.fieldName)
        );
        dispatch(actions.updatePOChangeReportInputValues(data));
        dispatch(actions.updatePOChangeReportFields(poChangeReportFieldList
          .map((field) => field.key)
          .filter((field) => appliedPoChangeSearchField.has(field))));
      } else {
        requestData = state.poChangeReport.poChangeReportRequestBody.search;
      }

      let requestBody = (state.poChangeReport.poChangeReportRequestBody) ? {
        ...state.poChangeReport.poChangeReportRequestBody,
        search: requestData,
        offset: '0',
        ...modifiedrequestBody
      } : {
        fields: fieldsForRequest,
        search: requestData,
        filter: [],
        count: appConfig.SearchThresholdLimit,
        offset: '0',
        ...modifiedrequestBody
      };
      requestBody = setValidFields(poChangeReportFieldProperties, requestBody);

      request({
        api: 'poChangeReportSearch',
        method: 'post',
        data: requestBody,
        cancellable: true
      }, dispatch, getState)
        .then((response) => {
          dispatch(cancelRequest());
          dispatch(actions.updatePOChangeReportRequestBody(requestBody));
          dispatch(toggleSpinner(false));
          if (callback) {
            callback(response.data);
          }
          dispatch(actions.updatePOChangeReportRowsPerPage(rowsPerPageOption));
          dispatch(actions.updatePOChangeReportResults(response));
        })
        .catch((error) => {
          dispatch(toggleSpinner(false));
          if (callback) {
            callback(null, error);
          }
        });
    }
  );
}

/**
 * Creates a thunk that exports the current PO change report through poChangeReportExportLargeFile.
 */
function exportPoChangeReport() {
  return createAsyncThunk(
    `${name}/exportPoChangeReport`,
    async ({
      callback,
      filetype
    }, { getState, dispatch }) => {
      dispatch(toggleSpinner(true));
      const state = getState();
      const searchRequestBody = {
        ...state.poChangeReport.poChangeReportRequestBody
      };
      const poChangeReportRequestBody = {
        search: [...(searchRequestBody.search) || []],
        fields: [...(searchRequestBody.fields) || []].filter((field: string) => (
          // Export omits fields configured as hidden from downloaded files.
          !appConfig.excludedPOReportFieldsInExportedFile.includes(field)
        )),
        filter: [...(searchRequestBody.filter) || []],
        searchType: searchRequestBody.searchType,
        fileFormat: filetype
      };

      request({
        api: 'poChangeReportExportLargeFile',
        method: 'post',
        data: poChangeReportRequestBody
      }, dispatch, getState)
        .then((response) => {
          callback(response);
          dispatch(toggleSpinner(false));
        })
        .catch((error) => {
          callback(null, error);
          dispatch(toggleSpinner(false));
        });
    }
  );
}

/**
 * Creates a thunk that executes a saved PO change report search by ID.
 */
function fetchSavedPOChangeReportResults() {
  return createAsyncThunk(
    `${name}/fetchSavedPOChangeReportResults`,
    async ({
      poChangeReportRequestData,
      callback
    }, { getState, dispatch }) => {
      dispatch(toggleSpinner(true));
      const state = getState();
      const {
        rowPerPageOption: rowsPerPageOption
      } = state.searchpaneldata || {};
      // Saved searches always execute from the first result window.
      let requestBody = { ...poChangeReportRequestData.searchCriteria, offset: '0' };
      requestBody = setValidFields(poChangeReportFieldProperties, requestBody);

      dispatch(updateDialogStates({ executeBookmarkSpinner: true }));
      request({
        api: 'poChangeReportSearch',
        method: 'post',
        data: {
          savedSearchID: poChangeReportRequestData.id,
          ...requestBody
        },
        cancellable: true
      }, dispatch, getState)
        .then((response) => {
          const fields = mapRequestToInputFields(
            [...poChangeReportRequestData.searchCriteria.search]
          );
          const poChangeSearchFields = new Set(
            poChangeReportRequestData.searchCriteria.search.map((field) => field.fieldName)
          );
          dispatch(actions.updatePOChangeReportInputValues(
            fields
          ));
          dispatch(actions.updatePOChangeReportRequestBody(requestBody));
          dispatch(actions.updatePOChangeReportFields(poChangeReportFieldList
            .map((field) => field.key)
            .filter((field) => poChangeSearchFields.has(field))));
          dispatch(actions.updatePOChangeReportResults(response));
          dispatch(actions.updatePOChangeReportSearchId());
          dispatch(actions.updatePOChangeReportPage(0));
          dispatch(actions.updatePOChangeReportRowsPerPage(rowsPerPageOption));
          dispatch(actions.updatePOChangeReportBookmarkId(poChangeReportRequestData.id));
          dispatch(actions.updatePOChangeReportBookmarkName(poChangeReportRequestData.name));
          dispatch(toggleSpinner(false));
          dispatch(updateDialogStates({ executeBookmarkSpinner: false }));
          if (callback) {
            callback(response);
          }
        })
        .catch((error) => {
          dispatch(toggleSpinner(false));
          dispatch(updateDialogStates({ executeBookmarkSpinner: false }));
          console.log(error);
          if (callback) {
            callback(null, error);
          }
        });
    }
  );
}

/**
 * Creates a thunk that reloads PO change report results from offset zero.
 */
function fetchPoChangereportsResultsOffset() {
  return createAsyncThunk(
    `${name}/fetchPoChangereportsResultsOffset`,
    async (_, { getState, dispatch }) => {
      dispatch(toggleSpinner(true));

      const state = getState();
      if (state.poChangeReport.poChangeReportRequestBody
        && state.poChangeReport.poChangeReportResultData) {
        let poChangeReportUpdatedRequest = state.poChangeReport.poChangeReportRequestBody;
        poChangeReportUpdatedRequest = {
          ...poChangeReportUpdatedRequest,
          offset: '0'
        };

        request({
          api: 'poChangeReportSearch',
          method: 'post',
          data: poChangeReportUpdatedRequest
        }, dispatch, getState)
          .then((response) => {
            dispatch(actions.PoChangeReportOffset(response));
            dispatch(toggleSpinner(false));
            dispatch(actions.updatePOChangeReportSearchId());
          })
          .catch((error) => {
            console.log('NEXT Error ', error);
            dispatch(toggleSpinner(false));
          });
      }
    }
  );
}

/**
 * Creates a thunk wrapper for resetting PO change report data to the first offset.
 */
function PoChangeReportDataOffset() {
  return createAsyncThunk(
    `${name}/PoChangeReportDataOffset`,
    async ({
      pofetchPoChangereportsResultsOffset
    }, { dispatch }) => {
      dispatch(pofetchPoChangereportsResultsOffset());
    }
  );
}

/**
 * Creates a thunk that prefetches next PO change report results for jump-to-page navigation.
 */
function fetchPoChangeReportJumpToPageNextResultSet() {
  return createAsyncThunk(
    `${name}/fetchPoChangeReportJumpToPageNextResultSet`,
    async (_, { getState, dispatch }) => {
      const state = getState();
      dispatch(actions.updateApiCallAfterAppend(false));
      if (state.poChangeReport.poChangeReportRequestBody
        && state.poChangeReport.poChangeReportResultData?.pages?.next) {
        const currentResultSet = state.poChangeReport.poChangeReportResultData;
        let updatedRequest = state.poChangeReport.poChangeReportRequestBody;
        const searchResultNextOffset = state.poChangeReport.poChangeReportResultData.pages.next;
        const resNext = searchResultNextOffset.indexOf(',');
        updatedRequest = {
          ...updatedRequest,
          // API cursor text contains the numeric offset before the first comma.
          offset: searchResultNextOffset.slice(7, resNext)
        };

        dispatch(togglePaginationSpinner({ isFetchingNextData: true }));
        request({
          api: 'poChangeReportSearch',
          method: 'post',
          data: updatedRequest,
          cancellable: true
        }, dispatch, getState)
          .then((response) => {
            dispatch(actions.updatePOChangeReportRequestBodyOnSameBookmark(updatedRequest));
            dispatch(togglePaginationSpinner({ isFetchingNextData: false }));
            dispatch(toggleSpinner(false));
            dispatch(actions.updatePOChangeReportNextResult({ response, currentResultSet }));
          })
          .catch((error) => {
            dispatch(togglePaginationSpinner({ isFetchingNextData: false }));
            console.log('NEXT Error ', error);
          });
      }
    }
  );
}

/**
 * Creates a thunk that loads the next PO change report result window.
 */
function fetchPoChangeReportNextResultSet() {
  return createAsyncThunk(
    `${name}/fetchPoChangeReportNextResultSet`,
    async (_, { getState, dispatch }) => {
      const state = getState();
      if (state.poChangeReport.poChangeReportRequestBody
        && state.poChangeReport.poChangeReportResultData?.pages?.next) {
        const currentResultSet = state.poChangeReport.poChangeReportResultData;
        const currentJumpToPage = state.poChangeReport.jumpToPage || 0;
        // Results are fetched in 1000-row windows, so jump pages map to window boundaries.
        const pageGroupSize = 1000 / state.poChangeReport.rowsPerPage;
        const pageGroup = currentJumpToPage > 0
          ? Math.floor(currentJumpToPage / pageGroupSize) : 0;
        const { triggerJumpToPage } = state.poChangeReport;
        if (triggerJumpToPage) {
          dispatch(actions.updateTriggerJumpToPage(false));
        }
        let updatedRequest = state.poChangeReport.poChangeReportRequestBody;
        const searchResultNextOffset = state.poChangeReport.poChangeReportResultData.pages.next;
        const resNext = searchResultNextOffset.indexOf(',');
        updatedRequest = {
          ...updatedRequest,
          offset: triggerJumpToPage
            // Jump navigation starts at the computed window; regular paging follows the API cursor.
            ? (pageGroup * 1000).toString() : searchResultNextOffset.slice(7, resNext)
        };

        dispatch(togglePaginationSpinner({ isFetchingNextData: true }));
        request({
          api: 'poChangeReportSearch',
          method: 'post',
          data: updatedRequest,
          cancellable: true
        }, dispatch, getState)
          .then((response) => {
            dispatch(actions.updatePOChangeReportRequestBodyOnSameBookmark(updatedRequest));
            dispatch(togglePaginationSpinner({ isFetchingNextData: false }));
            dispatch(toggleSpinner(false));
            dispatch(actions.updatePOChangeReportNextResult({ response, currentResultSet }));
            if (triggerJumpToPage) {
              dispatch(actions.ChangeReportAppendJumpToPageNextResultSet(
                state.poChangeReport.rowsPerPage
              ));
              dispatch(actions.updatePreviousResultSettoNull());
            }
          })
          .catch((error) => {
            dispatch(togglePaginationSpinner({ isFetchingNextData: false }));
            console.log('NEXT Error ', error);
          });
      }
    }
  );
}

/**
 * Creates a thunk wrapper for loading the next PO change report result window.
 */
function ChangeReportsDataNextSet() {
  return createAsyncThunk(
    `${name}/ChangeReportsDataNextSet`,
    async ({
      pofetchPoChangeReportNextResultSet
    }, { dispatch }) => {
      dispatch(pofetchPoChangeReportNextResultSet());
    }
  );
}

/**
 * Creates a thunk that prefetches previous PO change report results for jump-to-page navigation.
 */
function fetchReportJumpToPagePrevResultSet() {
  return createAsyncThunk(
    `${name}/fetchReportJumpToPagePrevResultSet`,
    async (_, { getState, dispatch }) => {
      const state = getState();
      dispatch(actions.updateApiCallBeforePrepend(false));
      if (state.poChangeReport?.poChangeReportResultData?.pages?.prev) {
        const currentResultSet = state.poChangeReport.poChangeReportResultData;
        let updatedRequest = state.poChangeReport.poChangeReportRequestBody;
        const searchResultPrevOffset = state.poChangeReport.poChangeReportResultData.pages.prev;
        const res = searchResultPrevOffset.indexOf(',');
        updatedRequest = {
          ...updatedRequest,
          offset: searchResultPrevOffset.slice(7, res)
        };

        dispatch(togglePaginationSpinner({ isFetchingPrevData: true }));
        request({
          api: 'poChangeReportSearch',
          method: 'post',
          data: updatedRequest,
          cancellable: true
        }, dispatch, getState)
          .then((response) => {
            dispatch(actions.updatePOChangeReportRequestBodyOnSameBookmark(updatedRequest));
            dispatch(togglePaginationSpinner({ isFetchingPrevData: false }));
            dispatch(toggleSpinner(false));
            dispatch(actions.updatePOChangeReportPrevResult({ response, currentResultSet }));
          })
          .catch((error) => {
            dispatch(togglePaginationSpinner({ isFetchingPrevData: false }));
            console.log('PREV Error ', error);
          });
      }
    }
  );
}

/**
 * Creates a thunk that loads the previous PO change report result window.
 */
function fetchReportPrevResultSet() {
  return createAsyncThunk(
    `${name}/fetchReportPrevResultSet`,
    async (_, { getState, dispatch }) => {
      const state = getState();
      if (state.poChangeReport?.poChangeReportResultData?.pages?.prev) {
        const currentResultSet = state.poChangeReport.poChangeReportResultData;
        const currentJumpToPage = state.poChangeReport.jumpToPage || 0;
        const pageGroupSize = 1000 / state.poChangeReport.rowsPerPage;
        const pageGroup = currentJumpToPage > 0
          ? Math.floor(currentJumpToPage / pageGroupSize) : 0;
        const { triggerJumpToPage } = state.poChangeReport;
        if (triggerJumpToPage) {
          dispatch(actions.updateTriggerJumpToPage(false));
        }
        let updatedRequest = state.poChangeReport.poChangeReportRequestBody;
        const searchResultPrevOffset = state.poChangeReport.poChangeReportResultData.pages.prev;
        const res = searchResultPrevOffset.indexOf(',');
        updatedRequest = {
          ...updatedRequest,
          offset: triggerJumpToPage
            // Jump navigation starts at the computed window; regular paging follows the API cursor.
            ? (pageGroup * 1000).toString() : searchResultPrevOffset.slice(7, res)
        };

        dispatch(togglePaginationSpinner({ isFetchingPrevData: true }));
        request({
          api: 'poChangeReportSearch',
          method: 'post',
          data: updatedRequest,
          cancellable: true
        }, dispatch, getState)
          .then((response) => {
            dispatch(actions.updatePOChangeReportRequestBodyOnSameBookmark(updatedRequest));
            dispatch(togglePaginationSpinner({ isFetchingPrevData: false }));
            dispatch(toggleSpinner(false));
            dispatch(actions.updatePOChangeReportPrevResult({ response, currentResultSet }));
            if (triggerJumpToPage) {
              dispatch(actions.ChangeReportAppendJumpToPagePrevResultSet(
                state.poChangeReport.rowsPerPage
              ));
              dispatch(actions.updateNextResultSettoNull());
            }
          })
          .catch((error) => {
            dispatch(togglePaginationSpinner({ isFetchingPrevData: false }));
            console.log('PREV Error ', error);
          });
      }
    }
  );
}

/**
 * Creates a thunk wrapper for loading the previous PO change report result window.
 */
function ChangeReportsDataPrevSet() {
  return createAsyncThunk(
    `${name}/ChangeReportsDataPrevSet`,
    async ({
      pofetchReportPrevResultSet
    }, { dispatch }) => {
      dispatch(pofetchReportPrevResultSet());
    }
  );
}

/**
 * Instantiates async PO change report actions exposed by the slice.
 */
function createExtraActions() {
  return {
    fetchPoChangeReports: fetchPoChangeReports(),
    exportPoChangeReport: exportPoChangeReport(),
    fetchSavedPOChangeReportResults: fetchSavedPOChangeReportResults(),
    fetchPoChangereportsResultsOffset: fetchPoChangereportsResultsOffset(),
    PoChangeReportDataOffset: PoChangeReportDataOffset(),
    fetchPoChangeReportNextResultSet: fetchPoChangeReportNextResultSet(),
    ChangeReportsDataNextSet: ChangeReportsDataNextSet(),
    fetchReportPrevResultSet: fetchReportPrevResultSet(),
    ChangeReportsDataPrevSet: ChangeReportsDataPrevSet(),
    fetchPoChangeReportJumpToPageNextResultSet: fetchPoChangeReportJumpToPageNextResultSet(),
    fetchReportJumpToPagePrevResultSet: fetchReportJumpToPagePrevResultSet()
  };
}

export const extraActions = createExtraActions();

export const poChangeReportActions = { ...actions, ...extraActions };
export const poChangeReportReducer = slice.reducer;
